import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { SelectBoxV2 } from "../../../../../../components/SelectBoxV2/SelectBoxV2";
import { TextField } from "../../../../../../components/TextFields/TextFields";
import {
  useCountriesList,
  useStatesList,
} from "../../../../../../util/Locations";
import {
  AddressMode,
  AddressValue,
  prepareAddressForForm,
} from "./address.utils";
import { getPhoneCodesOptions } from "../../../../../../util/phone";
import { Flex, Flex1, Flex2 } from "../../../../../../layout/FormLayout";
import { TextMdSemibold } from "../../../../../../components/Typography/TypographyV2";
import styled from "styled-components";
import { isValidPhoneNumber } from "../../../../../../util/util";
import { StylesConfig } from "react-select";
import { OptionType } from "../../../../../../types/types";
import { spacingTokens } from "../../../../../../designTokens";

const selectStyles: StylesConfig<OptionType, false> = {
  placeholder: (_base, state) => ({
    whiteSpace: state.selectProps.value ? "nowrap" : "normal",
  }),

  clearIndicator: (base) => ({
    ...base,
    padding: 0,
  }),
};

const HorizontalSeparator = styled.hr`
  border: none;
  border-top: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.secondaryBorder};
  width: 100%;
`;

export const FormContainer = styled.div`
  max-width: 600px;
  gap: 15px;
  display: flex;
  padding: ${({ theme }) => theme.resolve("spacing_2xl")};
  border-radius: ${({ theme }) => theme.resolve("radius_md")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.secondaryBorder};
  flex-direction: column;
`;

const RESET_FIELDS = [
  "address1",
  "address2",
  "address3",
  "city",
  "postal_code",
];

const ADDRESS_FIELDS: (keyof AddressValue)[] = [
  "company_name",
  "address1",
  "address2",
  "address3",
  "city",
  "postal_code",
  "crm_id",
  "tax_id",
  "location_id",
  "poc_first_name",
  "poc_last_name",
  "poc_email",
  "poc_phone",
];

export interface AddressAttributeFormProps {
  fieldPrefix: string;
  mode: AddressMode;
  savedValue: AddressValue | undefined;
  attributeName: string;
  isNotApplicable: boolean;
  disabled?: boolean;
}

interface BasicAddressFieldsProps {
  f: (name: string) => string;
  isDisabled: boolean;
  isCountrySelected: boolean;
  fieldPrefix: string;
  isAnyFieldFilled: boolean;
  states: { label: string; value: string }[];
}

interface AdvancedSectionProps {
  f: (name: string) => string;
  isDisabled: boolean;
  fieldPrefix: string;
}

const BasicAddressFields = memo(
  ({
    f,
    fieldPrefix,
    states,
    isDisabled = false,
    isAnyFieldFilled,
    isCountrySelected,
  }: BasicAddressFieldsProps) => {
    const { t } = useTranslation();
    const { register, control, formState, errors } = useFormContext();
    const isRequired = !isDisabled && isAnyFieldFilled;
    const countries = useCountriesList();
    const errorMessage = isRequired ? t("This field is required") : false;
    return (
      <>
        <TextField
          name={f("company_name")}
          label={`${t("Company Name")} (${t("optional")})`}
          theref={register({ required: false })}
          formState={formState}
          errors={errors}
          type="text"
          disabled={isDisabled}
        />

        <Controller
          control={control}
          name={f("country")}
          rules={{ required: errorMessage }}
          defaultValue={null}
          render={({ onChange, value }) => (
            <SelectBoxV2
              value={value}
              options={countries}
              placeholder={t("Country")}
              onChange={onChange}
              errors={errors}
              error={errors[fieldPrefix]?.country}
              isClearable
              formState={formState}
              disabled={isDisabled}
            />
          )}
        />

        <TextField
          name={f("address1")}
          label={t("Address Line 1")}
          theref={register({
            required: errorMessage,
          })}
          error={errors[fieldPrefix]?.address1}
          formState={formState}
          errors={errors}
          type="text"
          disabled={!isCountrySelected || isDisabled}
        />
        <TextField
          name={f("address2")}
          label={t("Address Line 2")}
          theref={register({ required: false })}
          formState={formState}
          errors={errors}
          type="text"
          disabled={!isCountrySelected || isDisabled}
        />
        <TextField
          name={f("address3")}
          label={t("Address Line 3")}
          theref={register({ required: false })}
          formState={formState}
          errors={errors}
          type="text"
          disabled={!isCountrySelected || isDisabled}
        />
        <Controller
          control={control}
          name={f("state")}
          rules={{ required: errorMessage }}
          defaultValue={null}
          render={({ onChange, value }) => (
            <SelectBoxV2
              value={value}
              options={states}
              onChange={onChange}
              placeholder={t("State")}
              isClearable
              error={errors[fieldPrefix]?.state}
              errors={errors}
              formState={formState}
              disabled={!isCountrySelected || isDisabled}
            />
          )}
        />
        <TextField
          name={f("city")}
          label={t("City")}
          error={errors[fieldPrefix]?.city}
          theref={register({
            required: errorMessage,
          })}
          formState={formState}
          errors={errors}
          type="text"
          disabled={!isCountrySelected || isDisabled}
        />
        <TextField
          name={f("postal_code")}
          label={t("Postal Code")}
          theref={register({
            required: errorMessage,
          })}
          formState={formState}
          errors={errors}
          type="text"
          error={errors[fieldPrefix]?.postal_code}
          disabled={!isCountrySelected || isDisabled}
        />
      </>
    );
  }
);

const AdvancedSection = memo(
  ({ f, isDisabled = false, fieldPrefix }: AdvancedSectionProps) => {
    const { t } = useTranslation();
    const { register, errors, formState, control, getValues } =
      useFormContext();
    return (
      <>
        {/* Tax ID */}
        <TextField
          name={f("tax_id")}
          label={t("Tax ID")}
          theref={register({ required: false })}
          formState={formState}
          errors={errors}
          type="text"
          disabled={Boolean(isDisabled)}
        />
        {/* CRM ID */}
        <TextField
          name={f("crm_id")}
          label={t("CRM ID")}
          theref={register({ required: false })}
          formState={formState}
          errors={errors}
          type="text"
          disabled={Boolean(isDisabled)}
        />
        {/* Location ID */}
        <TextField
          name={f("location_id")}
          label={t("Location ID")}
          theref={register({ required: false })}
          formState={formState}
          errors={errors}
          type="text"
          disabled={Boolean(isDisabled)}
        />

        <FormContainer>
          <TextMdSemibold
            style={{
              margin: `${spacingTokens.spacing_xs} ${spacingTokens.spacing_none} ${spacingTokens.spacing_xs} 0`,
            }}
          >
            {t("Point of Contact")}
          </TextMdSemibold>
          <HorizontalSeparator
            style={{ margin: spacingTokens.spacing_xxs }}
          ></HorizontalSeparator>
          <TextField
            name={f("poc_first_name")}
            label={t("First Name")}
            theref={register({ required: false })}
            formState={formState}
            errors={errors}
            type="text"
            disabled={Boolean(isDisabled)}
          />
          <TextField
            name={f("poc_last_name")}
            label={t("Last Name")}
            theref={register({ required: false })}
            formState={formState}
            errors={errors}
            type="text"
            disabled={Boolean(isDisabled)}
          />
          <TextField
            name={f("poc_email")}
            label={t("Email")}
            theref={register({
              required: false,
              validate: (value: string) => {
                if (!value) return true;
                return (
                  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ||
                  t("Please enter a valid email")
                );
              },
            })}
            formState={formState}
            errors={errors}
            type="text"
            disabled={Boolean(isDisabled)}
          />
          <Flex style={{ alignItems: "stretch" }}>
            <Flex1>
              <Controller
                as={SelectBoxV2}
                control={control}
                defaultValue={null}
                name={f("poc_country_code")}
                autoComplete="countryCode"
                placeholder={t("Country Code")}
                options={getPhoneCodesOptions()}
                rules={{
                  required: false,
                }}
                errors={errors}
                formState={formState}
                height={49}
                minHeight={49}
                disabled={isDisabled}
                isClearable
                styles={selectStyles}
              />
            </Flex1>
            <Flex2 style={{ marginRight: 0, marginLeft: "10px" }}>
              <TextField
                name={f("poc_phone")}
                autoComplete="tel"
                label={t("Phone Number")}
                theref={register({
                  required: false,
                  validate: (value: string) => {
                    if (!value) return true;
                    const poc_country_code = getValues(f("poc_country_code"));
                    if (!poc_country_code) {
                      return t("Please select a country code");
                    }
                    return (
                      isValidPhoneNumber({
                        phone_number: value,
                        country_code: poc_country_code,
                      }) || t("Please enter a valid phone number")
                    );
                  },
                })}
                formState={formState}
                errors={errors}
                error={errors[fieldPrefix]?.poc_phone}
                warningText={errors[fieldPrefix]?.poc_phone?.message}
                type="tel"
                disabled={Boolean(isDisabled)}
              />
            </Flex2>
          </Flex>
        </FormContainer>
      </>
    );
  }
);

export const AddressAttributeForm = ({
  fieldPrefix,
  mode,
  savedValue,
  attributeName,
  isNotApplicable = false,
  disabled = false,
}: AddressAttributeFormProps) => {
  const { setValue, control, clearErrors } = useFormContext();
  const f = useCallback(
    (name: string) => (fieldPrefix ? `${fieldPrefix}.${name}` : name),
    [fieldPrefix]
  );
  const seededCountryRef = useRef<string | null>(null);
  const prevCountryRef = useRef<string | null>(null);
  const isAdvanced = mode === "advanced";
  const selectedCountry = useWatch({ control, name: f("country") }) as {
    label: string;
    value: string;
  } | null;

  const isCountrySelected =
    Boolean(selectedCountry?.value) || Boolean(savedValue?.country_code);
  const states = useStatesList(
    selectedCountry?.value ?? savedValue?.country_code
  );

  const watchedAddressFieldsRaw = useWatch({
    control,
    name: ADDRESS_FIELDS.map(f),
  }) as Record<string, unknown>;

  const isAnyFieldFilled = useMemo(() => {
    return (
      Boolean(selectedCountry?.value) ||
      Object.values(watchedAddressFieldsRaw || {}).some(
        (v) => v !== undefined && v !== null && v !== ""
      )
    );
  }, [selectedCountry?.value, watchedAddressFieldsRaw]);

  //is not applicable case.
  useEffect(() => {
    if (!isNotApplicable) return;
    ADDRESS_FIELDS.forEach((field) => setValue(f(field), ""));
    setValue(f("country"), null);
    setValue(f("state"), null);
    setValue(f("poc_country_code"), null);
  }, [isNotApplicable, f, setValue]);

  useEffect(() => {
    if (!savedValue || Object.keys(savedValue).length === 0) return;
    ADDRESS_FIELDS.forEach((field) => {
      if (savedValue[field] !== undefined) {
        setValue(f(field), savedValue[field]);
      }
    });
    const prepared = prepareAddressForForm(savedValue);
    if (prepared.country) {
      seededCountryRef.current = prepared.country.value;
      setValue(f("country"), prepared.country);
    }
    if (prepared.poc_country_code) {
      setValue(f("poc_country_code"), prepared.poc_country_code);
    }
    if (prepared.state) {
      setValue(f("state"), prepared.state);
    }
  }, [savedValue, setValue, f]);

  // clear fields if country is updated
  useEffect(() => {
    const currentValue = selectedCountry?.value ?? null;
    if (currentValue === prevCountryRef.current) return;

    const previousValue = prevCountryRef.current;
    prevCountryRef.current = currentValue;

    if (!previousValue) return;

    if (currentValue === seededCountryRef.current) {
      seededCountryRef.current = null;
      return;
    }

    RESET_FIELDS.forEach((field) => setValue(f(field), ""));
    setValue(f("state"), null);
  }, [selectedCountry?.value, f, setValue]);

  useEffect(() => {
    if (!isAnyFieldFilled) {
      clearErrors([
        f("country"),
        f("address1"),
        f("city"),
        f("state"),
        f("postal_code"),
      ]);
    }
  }, [isAnyFieldFilled, clearErrors, f]);

  return (
    <FormContainer>
      <TextMdSemibold style={{ marginTop: spacingTokens.spacing_md }}>
        {attributeName}
      </TextMdSemibold>
      <HorizontalSeparator
        style={{ margin: spacingTokens.spacing_xxs }}
      ></HorizontalSeparator>
      <BasicAddressFields
        states={states}
        isAnyFieldFilled={isAnyFieldFilled}
        f={f}
        fieldPrefix={fieldPrefix}
        isDisabled={disabled}
        isCountrySelected={isCountrySelected}
      />
      {isAdvanced && (
        <AdvancedSection
          fieldPrefix={fieldPrefix}
          f={f}
          isDisabled={disabled}
        />
      )}
    </FormContainer>
  );
};
